import { useEffect, useRef, useState } from 'react';

/**
 * Señales visuales de escucha de la barra viva del modo charla (las prende la
 * app al pasarle `turnClosedTick` al composer). Dos gestos, sin sonido:
 *
 *   - **Asentir en la pausa**: cuando el alumno habló y hace una pausa de
 *     ~0,5 s, el punto de la barra asiente una vez. Se calcula en el teléfono
 *     con el RMS del mic (el server recién cierra el turno ~2 s después del
 *     fin de la voz: VAD de Scribe), así llega mientras corre esa espera.
 *     Es un acuse, no un cierre: si el alumno sigue, el turno sigue.
 *   - **Te escuché**: cuando el server confirma el turno (transcript final),
 *     la barra pasa a un estado propio, distinto de «pensando», que se
 *     sostiene al menos {@link HEARD_MIN_MS} y cede a «pensando» / «hablando».
 *     Sin esto, entre el cierre del turno y el arranque del LLM la barra
 *     seguía diciendo «Te escucho…» con el turno ya cerrado.
 *
 * Umbrales del mic: los del noise gate del worklet (`pcm-worklet.js`): voz
 * arriba de 0,025 de RMS, silencio abajo de 0,015. Entre los dos no cuenta
 * ni como voz ni como silencio (y corta la cuenta del silencio): un cuarto
 * con ruido de fondo en esa franja no dispara el gesto, igual que no cerraría
 * el gate.
 */

export interface PauseCueOptions {
  /** RMS desde el que una muestra cuenta como voz (abre el gate). */
  voiceRms: number;
  /** RMS debajo del cual una muestra cuenta como silencio (cierra el gate). */
  silenceRms: number;
  /** Voz acumulada necesaria para armar el gesto (descarta toses y clics). */
  minVoiceMs: number;
  /** Silencio continuo tras la voz que dispara el gesto. */
  pauseMs: number;
}

export const PAUSE_CUE_DEFAULTS: PauseCueOptions = {
  voiceRms: 0.025,
  silenceRms: 0.015,
  minVoiceMs: 250,
  pauseMs: 500,
};

/** Duración del gesto de asentir (la animación CSS dura lo mismo). */
export const NOD_MS = 720;
/** Mínimo que se sostiene «Te escuché» aunque el LLM arranque enseguida. */
export const HEARD_MIN_MS = 900;
/** Tope de «Te escuché» si el server nunca pasa a pensar (turno ignorado). */
export const HEARD_MAX_MS = 5000;
/** Cada cuánto se lee el RMS (el worklet lo publica cada ~50 ms). */
const SAMPLE_MS = 50;
/** Hueco máximo entre dos lecturas que se acredita (pestaña en segundo plano). */
const MAX_STEP_MS = 200;

export interface PauseCue {
  /** Suma una lectura del mic; `true` = disparar el gesto ahora. */
  push(rms: number, nowMs: number): boolean;
  /** Olvida la voz y el silencio acumulados (turno nuevo, mute, coach hablando). */
  reset(): void;
}

/**
 * Detector puro de «habló y ahora hace una pausa». Dispara UNA vez por pausa:
 * para volver a disparar, el alumno tiene que volver a hablar
 * `minVoiceMs`.
 */
export function createPauseCue(opts: PauseCueOptions = PAUSE_CUE_DEFAULTS): PauseCue {
  let voicedMs = 0;
  let silentMs = 0;
  let lastAt: number | null = null;
  return {
    push(rms, nowMs) {
      const dt = lastAt === null ? 0 : Math.min(MAX_STEP_MS, Math.max(0, nowMs - lastAt));
      lastAt = nowMs;
      if (rms >= opts.voiceRms) {
        voicedMs += dt;
        silentMs = 0;
        return false;
      }
      if (rms >= opts.silenceRms) {
        silentMs = 0;
        return false;
      }
      silentMs += dt;
      if (voicedMs >= opts.minVoiceMs && silentMs >= opts.pauseMs) {
        voicedMs = 0;
        return true;
      }
      return false;
    },
    reset() {
      voicedMs = 0;
      silentMs = 0;
      lastAt = null;
    },
  };
}

export type FreeStatus = 'listening' | 'thinking' | 'speaking';

export interface ListeningCuesInput {
  /** Modo charla activo y la app pasa `turnClosedTick`; si no, el hook no corre nada. */
  enabled: boolean;
  getEnergy: () => number;
  status: FreeStatus;
  muted: boolean;
  /**
   * Contador que la app sube cuando el server confirma un turno del alumno
   * (transcript final). Sólo importan sus CAMBIOS, no el valor.
   */
  turnClosedTick: number;
}

export interface ListeningCues {
  nodding: boolean;
  heard: boolean;
}

export function useListeningCues({
  enabled,
  getEnergy,
  status,
  muted,
  turnClosedTick,
}: ListeningCuesInput): ListeningCues {
  // ── Te escuché ────────────────────────────────────────────────────────────
  const [heardTick, setHeardTick] = useState<number | null>(null);
  const [heardMinDone, setHeardMinDone] = useState(false);
  const prevTickRef = useRef(turnClosedTick);
  useEffect(() => {
    const prev = prevTickRef.current;
    prevTickRef.current = turnClosedTick;
    if (!enabled || turnClosedTick === prev) return;
    setHeardTick(turnClosedTick);
    setHeardMinDone(false);
    const minT = setTimeout(() => setHeardMinDone(true), HEARD_MIN_MS);
    const maxT = setTimeout(() => setHeardTick(null), HEARD_MAX_MS);
    return () => {
      clearTimeout(minT);
      clearTimeout(maxT);
    };
  }, [enabled, turnClosedTick]);
  // Cede a «hablando» siempre; a «pensando» recién cumplido el mínimo.
  const heardShown =
    enabled &&
    heardTick !== null &&
    status !== 'speaking' &&
    !(heardMinDone && status !== 'listening');
  // Una vez que cedió, no vuelve: el próximo `listening` es el turno nuevo.
  useEffect(() => {
    if (heardTick !== null && !heardShown) setHeardTick(null);
  }, [heardTick, heardShown]);

  // ── Asentir en la pausa ───────────────────────────────────────────────────
  const [nodding, setNodding] = useState(false);
  const getEnergyRef = useRef(getEnergy);
  getEnergyRef.current = getEnergy;
  const detecting = enabled && status === 'listening' && !muted && !heardShown;
  useEffect(() => {
    if (!detecting) {
      setNodding(false);
      return;
    }
    const cue = createPauseCue();
    let nodTimer: ReturnType<typeof setTimeout> | null = null;
    const id = setInterval(() => {
      if (!cue.push(getEnergyRef.current(), performance.now())) return;
      setNodding(true);
      if (nodTimer !== null) clearTimeout(nodTimer);
      nodTimer = setTimeout(() => setNodding(false), NOD_MS);
    }, SAMPLE_MS);
    return () => {
      clearInterval(id);
      if (nodTimer !== null) clearTimeout(nodTimer);
    };
  }, [detecting]);

  return { nodding: detecting && nodding, heard: heardShown };
}
